import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ClientDealsHttpService } from '@services/http/partners/client-deals-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { describeClientError } from '@src/app/shared/utils/client-error-message';

/**
 * Comercial › Minha operação (Visão do Cliente, card 3461): entrada da rota `/dashboard/commercial/client`.
 *
 * Não há tela de lista (decisão do Breno, 19/10/2026): a rota abre direto a primeira operação das empresas do
 * usuário logado (papel COMPANY), na ordem de `GET commercial/client/deals`, e quem tem mais de uma troca pelo
 * seletor "Operação #N" do detalhe. `replaceUrl`, para o voltar do navegador não cair de novo aqui e redirecionar
 * em seguida. Esta tela só aparece carregando, com erro ou sem nenhuma operação.
 */
@Component({
  selector: 'app-client-deals',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './client-deals.component.html',
  styleUrl: './client-deals.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientDealsComponent implements OnInit {
  private readonly clientDealsService = inject(ClientDealsHttpService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly breadcrumbs: MenuItem[] = [
    { label: 'Comercial' },
    { label: 'Minha operação', routerLink: '/dashboard/commercial/client' },
  ];
  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };

  readonly loading = signal(true);
  readonly errorMessage = signal<string | null>(null);
  readonly isEmpty = signal(false);

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.clientDealsService
      .getMyDeals()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          const first = response?.[0];
          if (first) {
            this.router.navigate(['/dashboard/commercial/client', first.id], { replaceUrl: true });
            return;
          }
          this.isEmpty.set(true);
          this.loading.set(false);
        },
        error: error => {
          this.loading.set(false);
          this.errorMessage.set(
            describeClientError(
              error,
              'Não conseguimos carregar as suas operações agora. Confira a sua conexão e clique em Tentar de novo.'
            )
          );
        },
      });
  }
}
